import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CommonStepper from '../../../Components/CommonStepper';
import CommonButton from '../../../Components/CommonButton';
import CommonDropdown from '../../../Components/CommonDropdown';
import CommonLoader from '../../../Components/CommonLoader';
import CommonModal from '../../../Components/CommonModal';
import StatusBadge from '../../../Components/StatusBadge';
import { useToast } from '../../../Components/CommonToast';
import {
    risk_portal_risk_detail,
    risk_portal_workflow_state,
    risk_portal_workflow_config,
    risk_portal_get_settings,
    risk_portal_get_ack,
    risk_portal_get_quantification,
    risk_portal_get_bowtie,
    risk_portal_activity,
    risk_portal_advance_stage,
    risk_portal_request_ack,
    risk_portal_accept_risk,
    risk_portal_close_risk,
    risk_portal_add_comment,
    risk_portal_assess,
} from '../../../services';
import { heading3, bodySmall, Body, SubHeading } from '../../../Constants/Fonts';
import { extractItem, DEFAULT_BLUEPRINT, DEFAULT_STAGE_SECTIONS, titleCase, fmtDate, LIKELIHOOD_LABELS, IMPACT_LABELS, liOptions, ratingOf, ratingColor } from './RiskDetail/constants';
import { currentUserEmail } from '../workflow';
import AssessmentSection from '../Pages/RiskDetail/AssessmentSection';
import TreatmentSection from '../Pages/RiskDetail//TreatmentSection';
import EvidenceSection from '../Pages/RiskDetail//EvidenceSection';
import CommentsSection from '../Pages/RiskDetail//CommentsSection';
import NovaAiSection from '../Pages/RiskDetail//NovaAiSection';
import QuantificationSection from '../Pages/RiskDetail//QuantificationSection';
import BowtieSection from '../Pages/RiskDetail//BowtieSection';
import RiskReport from '../Pages/RiskDetail//RiskReport';
import PageHeading from '../../../Components/PageHeading';

// `riskId == null` opens a NEW risk on its first workflow stage: the stage-1
// panel is the Identify Risk form, and saving it creates the risk. This same
// screen then switches to the created risk (createdId) and the workflow carries on.
export default function RiskDetail({ riskId: riskIdProp, role, onBack, categories = [], businessUnits = [], projects = [] }) {
    const [createdId, setCreatedId] = useState(null);
    const riskId = createdId ?? riskIdProp;
    const creating = riskId == null;
    const [detail, setDetail] = useState(null);
    const [ws, setWs] = useState(null);
    const [stages, setStages] = useState(DEFAULT_BLUEPRINT);
    const [ack, setAck] = useState(null);
    const [quantification, setQuantification] = useState(null);
    const [bowtie, setBowtie] = useState(null);
    const [activity, setActivity] = useState([]);
    const [settings, setSettings] = useState(null);
    const [viewStage, setViewStage] = useState(null);   // stepper-selected stage to view; null → follow current
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showQuant, setShowQuant] = useState(false);   // "Risk Quantification" overlay (FAIR + bow-tie, standalone)
    const [quantTab, setQuantTab] = useState('fair');    // which analysis the user picked in that overlay
    // Review-stage re-scoring: the reviewer's chosen current (residual) likelihood
    // & impact. null = untouched (falls back to the saved residual / inherent).
    const [reviewL, setReviewL] = useState(null);
    const [reviewI, setReviewI] = useState(null);
    const [rejectOpen, setRejectOpen] = useState(false);   // Reject-plan reason modal

    const { notify, toastNode } = useToast();

    const load = useCallback(async () => {
        // No risk yet — only the workflow config + stage→section settings apply.
        if (riskId == null) {
            const [cfg, st] = (await Promise.allSettled([
                risk_portal_workflow_config(),
                risk_portal_get_settings(),
            ])).map((r) => (r.status === 'fulfilled' ? extractItem(r.value) : null));
            setDetail({});
            setSettings(st || null);
            if (cfg?.stages?.length) setStages([...cfg.stages].sort((a, b) => (a.order || 0) - (b.order || 0)));
            setLoading(false);
            return;
        }
        const [d, w, cfg, st, ak, q, bt, act] = (await Promise.allSettled([
            risk_portal_risk_detail(riskId),
            risk_portal_workflow_state(riskId),
            risk_portal_workflow_config(),
            risk_portal_get_settings(),
            risk_portal_get_ack(riskId),
            risk_portal_get_quantification(riskId),
            risk_portal_get_bowtie(riskId),
            risk_portal_activity(riskId),
        ])).map((r) => (r.status === 'fulfilled' ? extractItem(r.value) : null));

        setDetail(d || null);
        setWs(w || null);
        setSettings(st || null);
        setAck(ak || null);
        setQuantification(q || null);
        setBowtie(bt || null);
        setActivity(Array.isArray(act) ? act : (act?.results || []));
        if (cfg?.stages?.length) setStages([...cfg.stages].sort((a, b) => (a.order || 0) - (b.order || 0)));
        setLoading(false);
    }, [riskId]);

    // Initial load shows the full-page loader; reload() (after a mutation) is silent.
    const reload = useCallback(async () => { await load(); }, [load]);
    useEffect(() => { setLoading(true); load(); }, [load]);

    // Resolve the current stage + permitted transitions from the workflow config.
    const flow = useMemo(() => {
        const ids = stages.map((s) => s.id);
        const labels = Object.fromEntries(stages.map((s) => [s.id, s.name || s.label || titleCase(s.id)]));
        const owners = Object.fromEntries(stages.map((s) => [s.id, s.owner_role || null]));
        const transitions = Object.fromEntries(stages.map((s) => [s.id, Array.isArray(s.next_stages) ? s.next_stages : []]));
        let current = ws?.current_stage;
        if (!current || !ids.includes(current)) {
            const st = detail?.state;
            const fb = st === 'closed' ? 'closed' : st === 'accepted' ? 'monitoring' : st === 'in_progress' ? 'treatment' : ids[0];
            current = ids.includes(fb) ? fb : ids[0];
        }
        return { ids, labels, owners, transitions, current, currentIdx: ids.indexOf(current) };
    }, [stages, ws, detail]);

    const stepperSteps = useMemo(() => stages.map((s) => ({ id: s.id, label: s.name || s.label || titleCase(s.id) })), [stages]);
    const stageOwner = flow.owners[flow.current];
    const currentStageType = (stages.find((s) => s.id === flow.current) || {}).stage_type || null;
    // Who may act on the current stage: ONLY the role that owns it (per the
    // admin-configured workflow) — no blanket admin/CISO override. The
    // `risk_owner` stage is further scoped to the specific named owner (email),
    // not to everyone holding the risk_owner role. If a stage is owned by
    // risk_admin in the config, an admin acts on it because they OWN it — not
    // because of a hardcoded exception.
    const myEmail = currentUserEmail();
    const isNamedOwner = Boolean(myEmail) && String(detail?.risk_owner || '').toLowerCase() === myEmail;
    const canAct = Boolean(stageOwner) && role === stageOwner && (stageOwner !== 'risk_owner' || isNamedOwner);
    const nextStages = flow.transitions[flow.current] || [];
    // Resolve the forward target DYNAMICALLY so deleting or reordering stages
    // never sends a dangling id (e.g. a deleted "monitoring" the config still
    // points to). Prefer the configured next_stages[0] when it still exists in the
    // live pipeline; otherwise fall back to the next stage by order. null = this is
    // the terminal stage (advancing closes the risk).
    const nextStageId = nextStages.find((id) => flow.ids.includes(id))
        || (flow.currentIdx >= 0 && flow.currentIdx < flow.ids.length - 1 ? flow.ids[flow.currentIdx + 1] : null);

    // Reviewer "Reject Plan": on a review-type stage the owner can send the risk
    // BACK to the Treatment Planning stage for revision. The target is the
    // configured treatment-type stage (resolved by stage_type, not next_stages —
    // the workflow editor only tracks a single forward transition). Shown only to
    // the review stage's owner while they're viewing the current stage.
    const treatmentStageId = (stages.find((s) => s.stage_type === 'treatment') || {}).id || null;

    // Per-org stage→section mapping (settings.stage_sections) merged over the
    // default. Drives which panels show for the viewed stage.
    const stageSections = useMemo(
        () => ({ ...DEFAULT_STAGE_SECTIONS, ...(settings?.stage_sections || {}) }),
        [settings]
    );
    // The stage whose panels are shown. Defaults to the current workflow stage;
    // clicking a completed step in the stepper lets the user revisit it. Never a
    // future (locked) stage — clamp back to current if so.
    const viewedStage = (viewStage && flow.ids.includes(viewStage)) ? viewStage : flow.current;
    const viewedIdx = flow.ids.indexOf(viewedStage);
    const viewingOther = viewedStage !== flow.current;
    // A new risk's first stage is always its Identify form (the assessment panel
    // renders AddRisk), whatever the org maps to that stage for existing risks.
    const viewedKeys = creating ? ['assessment'] : (stageSections[viewedStage] || []);

    // The current stage is the LAST (terminal) stage when it has no outgoing
    // transitions. Submitting it closes the risk — dynamically, so removing the
    // `closed` stage just makes whatever ends the workflow the closing stage.
    const isLastStage = !nextStageId;
    // The single stage-progression control ("Save & Next" / "Save & Close Risk")
    // shows only for the current-stage owner while they're viewing that stage.
    // Whoever opened Create Risk fills the new risk's first stage.
    const canSubmit = creating || (canAct && !viewingOther);
    // The reject-back control shows for the review stage owner viewing it, as long
    // as a distinct treatment stage exists to return the risk to.
    const canReject = canSubmit && currentStageType === 'review' && !!treatmentStageId && treatmentStageId !== flow.current;
    const stageBtnText = isLastStage ? 'Save & Close Risk' : 'Save & Next →';

    // ── Review-stage re-scoring ──
    // On a review stage the owner re-scores the risk's CURRENT (residual)
    // likelihood & impact via the two dropdowns below. The chosen values fall back
    // to the saved residual, then the inherent (original) score. Comparing the two
    // tells the reviewer whether the risk improved after treatment or degraded /
    // became more critical.
    const onReviewStage = currentStageType === 'review';
    const asmt = detail?.assessment || {};
    const inhL = Number(asmt.inherent_likelihood ?? detail?.likelihood) || 0;
    const inhI = Number(asmt.inherent_impact ?? detail?.impact) || 0;
    const effL = reviewL ?? (Number(asmt.residual_likelihood) || inhL || 1);
    const effI = reviewI ?? (Number(asmt.residual_impact) || inhI || 1);
    const inhScore = inhL * inhI;
    const curScore = effL * effI;

    // Persist the re-score through the assess endpoint (writes residual_*; keeps
    // inherent + the other assessment fields intact). Runs as the Save & Next
    // saveFn so advancing records the reviewer's current rating.
    const saveReassessment = async () => {
        await risk_portal_assess(riskId, {
            inherent_likelihood: inhL || 1,
            inherent_impact: inhI || 1,
            residual_likelihood: effL,
            residual_impact: effI,
            control_effectiveness: asmt.control_effectiveness ?? '',
            risk_velocity: asmt.risk_velocity ?? '',
            notes: asmt.notes ?? '',
        });
    };

    const runOp = async (fn, okMsg) => {
        setBusy(true);
        try {
            await fn();
            if (okMsg) notify(okMsg, 'success');
            await reload();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Action failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    // The unified stage-progression action. `saveFn` (optional) runs the current
    // stage's own save API first; then we move the workflow forward — or, on the
    // terminal stage, close the risk. Everything is driven by the config: the
    // last stage (no next_stages) closes; an approval stage accepts the residual
    // risk; anything else advances to its forward stage. Sections call this from
    // their single "Save & Next" button; form-less stages use the fallback below.
    const submitStage = async (saveFn) => {
        setBusy(true);
        try {
            if (typeof saveFn === 'function') await saveFn();
            if (isLastStage) {
                await risk_portal_close_risk(riskId, {});
                notify('Risk closed', 'success');
                // Closing ends the workflow — return to the Risk Register listing.
                if (onBack) { onBack(); return; }
            } else if (currentStageType === 'approval') {
                await risk_portal_accept_risk(riskId, {});
                notify('Saved — residual risk accepted', 'success');
            } else {
                await risk_portal_advance_stage(riskId, { next_stage: nextStageId, acting_role: role });
                notify('Saved — moved to the next stage', 'success');
            }
            setViewStage(null);   // follow the new current stage
            await reload();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Action failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    // Reject the treatment plan from the Review stage: the reason comes from the
    // themed reject modal (see below). It's recorded as a comment so the owner sees
    // WHY it came back, then the risk moves to the Treatment Planning stage where
    // the owner can edit and resubmit.
    const doReject = async (reason) => {
        setRejectOpen(false);
        setBusy(true);
        try {
            await risk_portal_add_comment(riskId, { text: `Treatment plan rejected: ${reason.trim()}` });
            // `reason` is required by the backend for a backward (send-back) transition.
            await risk_portal_advance_stage(riskId, { next_stage: treatmentStageId, acting_role: role, reason: reason.trim() });
            notify('Plan rejected — sent back to Treatment Planning', 'success');
            setViewStage(null);   // follow the risk to its new current stage
            await reload();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Reject failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    // Standalone save for the "Risk Quantification" overlay (FAIR + bow-tie):
    // runs the section's own save API but does NOT advance the workflow. Passed
    // to those sections as their `submitStage` so their Save button just persists.
    const saveStandalone = async (saveFn) => {
        setBusy(true);
        try {
            if (typeof saveFn === 'function') await saveFn();
            notify('Saved', 'success');
            await reload();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Save failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    // Create mode: the new risk sits on the first stage, so its Save & Next both
    // creates it and — when the creator owns that stage — advances it, exactly as
    // the stage form would for an existing risk. Then load the created risk here.
    const handleCreated = async (data) => {
        const rid = data?.risk_id || '';
        if (data?.id == null) {
            notify(`Risk ${rid} created`.replace('  ', ' ').trim(), 'success');
            onBack?.();
            return;
        }
        const firstOwner = flow.owners[flow.ids[0]];
        const ownsFirst = Boolean(firstOwner) && role === firstOwner
            && (firstOwner !== 'risk_owner' || (Boolean(myEmail) && String(data?.risk_owner || '').toLowerCase() === myEmail));
        setBusy(true);
        try {
            if (ownsFirst && nextStageId) {
                await risk_portal_advance_stage(data.id, { next_stage: nextStageId, acting_role: role });
                notify(`Risk ${rid} created — moved to the next stage`.replace('  ', ' '), 'success');
            } else {
                notify(`Risk ${rid} created`.replace('  ', ' ').trim(), 'success');
            }
        } catch (e) {
            notify(`Risk ${rid} created, but it could not be moved to the next stage: ${e?.response?.data?.error?.message || e?.message || 'Action failed'}`, 'error');
        } finally {
            setBusy(false);
        }
        setViewStage(null);
        setCreatedId(data.id);
    };

    if (loading) return <CommonLoader fullPage label="Loading risk…" />;

    if (!detail) {
        return (
            <div className="rp1_detail">
                <CommonButton text="← Back to Risk List " className="rp1_link_btn" onClick={onBack} />
                <div className="rp1_widget" style={{ marginTop: 16 }}>Could not load this risk.</div>
                {toastNode}
            </div>
        );
    }

    // A closed risk is read-only — show the lifecycle report/log instead of the
    // editable stepper + stage forms.
    if (detail.state === 'closed') {
        return (
            <>
                <RiskReport detail={detail} stages={stages} activity={activity} onBack={onBack} />
                {toastNode}
            </>
        );
    }

    const ctx = { riskId, detail, role, reload, notify, submitStage, canSubmit, isLastStage, stageBtnText, busy, categories, businessUnits, projects, onCreated: handleCreated };

    return (
        <div className="rp1_detail">
          <div className="cmn_elevated_card" style={{ padding: 20 }}>

            {/* Header */}
            <div className="rp1_detail_header">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div>
                        <h1 style={heading3({ margin: 0 })}>{flow.labels[flow.current]}</h1>
                        <div style={{ marginTop: 4 }}>
                            <Body as="span" color="var(--color-primary)" weight="bold">{detail.risk_id} </Body>
                            <Body as="span" color="var(--color-text-primary)">{creating ? 'New risk' : detail.risk}</Body>
                        </div>
                    </div>
                    <div style={{ display: 'inline-flex', gap: 8, flexShrink: 0 }}>
                        {!creating && <CommonButton variant="secondary" text="Risk Quantification" onClick={() => setShowQuant(true)} />}
                        <CommonButton text="← Back to Risk List " className="rp1_link_btn" onClick={onBack} />
                    </div>
                </div>
            </div>

            {/* Workflow stepper + stage actions */}
            <div className="rp1_widget">
                    <CommonStepper
                        steps={stepperSteps}
                        currentIndex={viewedIdx}
                        // stepper follows the viewed stage; click any step to jump to it
                        // (a new risk has nothing to revisit until it's created)
                        onStepClick={creating ? undefined : (step, i) => setViewStage(i === flow.currentIdx ? null : step.id)}
                    />
                {/* Stage progression is no longer driven from here — each stage's
                    single "Save & Next" / "Save & Close Risk" button (in its form,
                    or the fallback below for form-less stages) both saves and
                    advances the workflow. */}
                <div className="rp1_stage_bar">
                    <div className="rp1_stage_bar_left">
                        {/* <Body as="span" className="rp1_stage_pill">{flow.labels[flow.current]}</Body> */}
                        {stageOwner && (
                            <span className="rp1_meta_item">
                                <Body as="span" variant={2} color="var(--color-text-muted)" className="rp1_meta_label">Stage Owner:</Body>
                                <Body as="span" variant={2} color="var(--color-text-primary)" weight="semibold">{titleCase(stageOwner)}</Body>
                            </span>
                        )}
                    </div>
                    <div className="rp1_stage_bar_actions">
                        {detail.risk_type && <StatusBadge variant="info" dot={false} label={titleCase(detail.risk_type)} />}
                        {detail.risk_owner && (
                            <span className="rp1_meta_item">
                                <Body as="span" variant={2} color="var(--color-text-muted)" className="rp1_meta_label">Risk Owner:</Body>
                                <Body as="span" variant={2} color="var(--color-text-secondary)">{detail.risk_owner}</Body>
                            </span>
                        )}
                        {detail.target_date && (
                            <span className="rp1_meta_item">
                                <Body as="span" variant={2} color="var(--color-text-muted)" className="rp1_meta_label">Target</Body>
                                <Body as="span" variant={2} color="var(--color-text-secondary)">{fmtDate(detail.target_date)}</Body>
                            </span>
                        )}
                        {ack?.status && (
                            <StatusBadge
                                variant={ack.status === 'acknowledged' ? 'success' : ack.status === 'declined' ? 'danger' : 'warning'}
                                label={ack.status === 'acknowledged' ? 'Ownership Acknowledged' : ack.status === 'declined' ? 'Declined' : 'Pending Acknowledgement'}
                            />
                        )}
                        {detail.risk_owner && ack?.status !== 'acknowledged' && !onReviewStage && (
                            <CommonButton variant="secondary" text={ack?.status === 'pending' ? 'Send Reminder' : 'Request Acknowledgement'}
                                onClick={() => runOp(() => risk_portal_request_ack(riskId), 'Acknowledgement request sent')} disable={busy} />
                        )}
                    </div>
                </div>
            </div>

            {/* Workflow panels — gated to the viewed stage (see stageSections).
                Each panel owns its own data + mutations. Every panel, including
                Comments, is stage-mapped now (Comments lives on the Closed stage).
                Section registry is keyed to match stageSections; unlisted keys
                simply don't render (only mounted ones fetch). */}
            {(() => {
               
                const rescoreNode = (onReviewStage && canSubmit) ? (
                    <div className="rp1_widget" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <PageHeading title="Calculate residual risk" />
                        {/* <Body as="span" variant={2} color="var(--color-text-secondary)">
                            Re-score the risk's current likelihood and impact to categorise it at review.
                        </Body> */}
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div style={{ flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <span className="rp1_field_label">Current Likelihood</span>
                                <CommonDropdown value={effL} onChange={(v) => setReviewL(Number(v))}
                                    options={liOptions(LIKELIHOOD_LABELS)} disabled={busy} minWidth={0} style={{ width: '100%' }} />
                            </div>
                            <div style={{ flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                <span className="rp1_field_label">Current Impact</span>
                                <CommonDropdown value={effI} onChange={(v) => setReviewI(Number(v))}
                                    options={liOptions(IMPACT_LABELS)} disabled={busy} minWidth={0} style={{ width: '100%' }} />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingBottom: 2 }}>
                                <Body as="span" weight="semibold" style={{
                                    fontSize: 13, padding: '5px 12px', borderRadius: 999,
                                    color: ratingColor(ratingOf(curScore)), border: `1px solid ${ratingColor(ratingOf(curScore))}`,
                                    background: 'var(--color-surface-muted)',
                                }}>
                                    {ratingOf(curScore)} · {curScore}
                                </Body>
                                {inhScore > 0 && curScore !== inhScore && (
                                    <Body as="span" variant={2} weight="semibold" color={curScore > inhScore ? 'var(--color-danger)' : 'var(--color-success)'}>
                                        {curScore > inhScore
                                            ? `▲ Degraded — more critical (was ${ratingOf(inhScore)} · ${inhScore})`
                                            : `▼ Improved — risk reduced (was ${ratingOf(inhScore)} · ${inhScore})`}
                                    </Body>
                                )}
                            </div>
                        </div>
                    </div>
                ) : null;

                const SECTION_NODES = {
                    assessment: <AssessmentSection {...ctx} />,
                    treatment: <TreatmentSection {...ctx} />,
                    evidence: <EvidenceSection {...ctx} rescoreSlot={rescoreNode} />,
                    novaai: <NovaAiSection {...ctx} />,
                    comments: <CommentsSection {...ctx} />,
                };
               
                const FORM_SECTIONS = ['assessment', 'treatment'];
                const hasFormSection = viewedKeys.some((k) => FORM_SECTIONS.includes(k));
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', rowGap: 16, marginTop: 16 }}>
                        {viewingOther && (
                            <div className="rp1_widget" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'var(--color-surface-muted)' }}>
                                <Body as="span" variant={2} color="var(--color-text-secondary)">
                                    Viewing the <strong>{flow.labels[viewedStage]}</strong> stage. The workflow is currently at <strong>{flow.labels[flow.current]}</strong>.
                                </Body>
                                <CommonButton variant="secondary" text={`Return to ${flow.labels[flow.current]} →`} onClick={() => setViewStage(null)} />
                            </div>
                        )}
                      
                        {(!canAct && !creating && !viewingOther) ? (
                            <div className="rp1_widget rp1_await_card">
                                <div className="rp1_await_main">
                                    <span className="rp1_await_icon" aria-hidden="true">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="9" />
                                            <path d="M12 7v5l3 2" />
                                        </svg>
                                    </span>
                                    <div className="rp1_await_text">
                                        <SubHeading variant={3} color="var(--color-text-primary)" style={{ margin: 0 }}>Awaiting response</SubHeading>
                                        <Body variant={2} color="var(--color-text-secondary)" style={{ margin: '2px 0 0' }}>
                                            This risk is in the <strong>{flow.labels[flow.current]}</strong> stage and can't be actioned by you yet.
                                        </Body>
                                    </div>
                                    <Body as="span" className="rp1_badge rp1_badge--warning rp1_await_pill">
                                        <span className="rp1_await_dot" /> Pending
                                    </Body>
                                </div>
                                <div className="rp1_await_note">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <circle cx="12" cy="12" r="9" />
                                        <path d="M12 11v5M12 8h.01" />
                                    </svg>
                                    <Body as="span" variant={2} color="var(--color-text-muted)" style={{ margin: 0 }}>
                                        You'll be able to act on this risk once it reaches a stage your role owns.
                                    </Body>
                                </div>
                            </div>
                        ) : (<>
                            {viewedKeys.map((k) => SECTION_NODES[k] && <React.Fragment key={k}>{SECTION_NODES[k]}</React.Fragment>)}
                            {viewedKeys.length === 0 && (
                                <div className="rp1_widget">
                                    <p style={bodySmall({ color: 'var(--color-text-muted)', margin: 0 })}>
                                        No form for the <strong>{flow.labels[viewedStage]}</strong> stage — use the button below to move on.
                                    </p>
                                </div>
                            )}
                            
                            {(canReject || (canSubmit && !hasFormSection)) && (
                                <div  style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    {canReject && (
                                        <CommonButton variant="error" text="Reject Plan" onClick={() => setRejectOpen(true)} disable={busy} />
                                    )}
                                    {canSubmit && !hasFormSection && (
                                        <CommonButton text={stageBtnText} onClick={() => submitStage(onReviewStage ? saveReassessment : null)} disable={busy} />
                                    )}
                                </div>
                            )}
                        </>)}
                      
                    </div>
                );
            })()}

          </div>

            {showQuant && (
                <div className="rp1_quant_overlay" onClick={() => setShowQuant(false)}>
                    <div className="rp1_quant_panel" onClick={(e) => e.stopPropagation()}>
                        <div className="rp1_quant_head">
                            <div>
                                <h2 style={heading3({ margin: 0 })}>Risk Quantification</h2>
                                <Body as="span" variant={2} color="var(--color-text-muted)">
                                    Choose an analysis for {detail.risk_id} — {detail.risk}.
                                </Body>
                            </div>
                            <CommonButton variant="secondary" text="Close" onClick={() => setShowQuant(false)} />
                        </div>

                        {/* Analysis chooser — the three options. */}
                        <div className="rp1_quant_tabs">
                            {QUANT_TABS.map((t) => (
                                <CommonButton
                                    key={t.id}
                                    variant={quantTab === t.id ? 'primary' : 'secondary'}
                                    text={t.label}
                                    onClick={() => setQuantTab(t.id)}
                                />
                            ))}
                        </div>

                        <div className="rp1_quant_body">
                            {quantTab === 'fair' && (
                                <QuantificationSection
                                    riskId={riskId} detail={detail} role={role} reload={reload} notify={notify}
                                    submitStage={saveStandalone} canSubmit stageBtnText="Save" busy={busy}
                                    quantification={quantification}
                                />
                            )}
                            {quantTab === 'bowtie' && (
                                <BowtieSection
                                    riskId={riskId} detail={detail} role={role} reload={reload} notify={notify}
                                    submitStage={saveStandalone} canSubmit stageBtnText="Save" busy={busy}
                                    bowtie={bowtie}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

           
            <CommonModal
                show={rejectOpen}
                onHide={() => setRejectOpen(false)}
                title="Reject Treatment Plan"
                subtitle="The risk returns to Treatment Planning for revision. Your reason is recorded as a comment the owner will see."
                fields={[{ name: 'reason', label: 'Reason for rejection', type: 'textarea', rows: 3, required: true, placeholder: 'Explain what needs to change…' }]}
                defaultValues={{ reason: '' }}
                onSubmit={(d) => doReject(d.reason)}
                buttons={[
                    { text: 'Cancel', variant: 'secondary', onClick: () => setRejectOpen(false), disabled: busy },
                    { text: 'Reject Plan', variant: 'danger', type: 'submit', loading: busy, loadingText: 'Rejecting…' },
                ]}
            />

            {busy && <CommonLoader fullPage label="Working…" />}
            {toastNode}
        </div>
    );
}

// The analyses offered in the standalone Risk Quantification overlay. FAIR
// includes its Monte Carlo simulation inline (no separate tab).
const QUANT_TABS = [
    { id: 'fair', label: 'Financial Quantification (FAIR)' },
    { id: 'bowtie', label: 'Bow-Tie Analysis' },
];
